/**
 * DemoMockProvider — moonshot mode runtime.
 *
 * Installs the real fetch interceptor (replacing the previous placeholder),
 * wires keyboard shortcuts for journey navigation (J/K/1..6), and renders a
 * croppable debug HUD in the top-right corner.
 *
 * Outside moonshot mode this component is a no-op — it renders its children
 * untouched and never touches `window.fetch`.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { installGlobalMockFetch, mockFetch as rawMockFetch } from './mockFetch';
import {
  JOURNEY_COUNT,
  JOURNEY_NAMES,
  getMoonshotState,
  parseMoonshotParams,
} from './useMoonshotMode';
import { journeyFixtures } from './fixtures';
import { BOOT_OVERLAY_Z_INDEX } from '../styles/layers';

export interface DemoMockContextValue {
  enabled: boolean;
  journey: number;
  journeyName: string;
  step: number;
  totalSteps: number;
  hudOff: boolean;
  setJourney: (n: number) => void;
  nextJourney: () => void;
  replayJourney: () => void;
  setStep: (n: number) => void;
  /** Direct access to mockFetch for components that opt-in (bypasses global install). */
  mockFetch: typeof rawMockFetch;
}

const DemoMockContext = createContext<DemoMockContextValue | null>(null);

export function useDemoMock(): DemoMockContextValue {
  const ctx = useContext(DemoMockContext);
  if (ctx) return ctx;
  // Safe defaults when the provider is absent (e.g., server-side, tests).
  return {
    enabled: false,
    journey: 1,
    journeyName: JOURNEY_NAMES[1],
    step: 1,
    totalSteps: 1,
    hudOff: false,
    setJourney: () => undefined,
    nextJourney: () => undefined,
    replayJourney: () => undefined,
    setStep: () => undefined,
    mockFetch: rawMockFetch,
  };
}

/** Writes `?journey=N` back into the URL without triggering navigation. */
function updateJourneyParam(journey: number): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('mode', 'moonshot');
    url.searchParams.set('journey', String(journey));
    window.history.replaceState({}, '', url.toString());
  } catch {
    // No-op if URL construction fails.
  }
}

export interface DemoMockProviderProps {
  /** Force-enable regardless of URL. Useful for Storybook/tests. */
  forceEnabled?: boolean;
  children: React.ReactNode;
}

export const DemoMockProvider: React.FC<DemoMockProviderProps> = ({ forceEnabled, children }) => {
  const initial = useMemo(() => {
    const s = getMoonshotState();
    return {
      enabled: forceEnabled || s.enabled,
      journey: s.journey,
      hudOff: s.hudOff,
    };
  }, [forceEnabled]);

  const [enabled] = useState<boolean>(initial.enabled);
  const [journey, setJourneyState] = useState<number>(initial.journey);
  const [step, setStepState] = useState<number>(1);
  const [hudOff] = useState<boolean>(initial.hudOff);
  const [replayNonce, setReplayNonce] = useState<number>(0);

  // Install global fetch interceptor once, when moonshot is enabled.
  useEffect(() => {
    if (!enabled) return;
    const uninstall = installGlobalMockFetch();
    // eslint-disable-next-line no-console
    console.info('[moonshot] DemoMockProvider active — journey=%d (%s)', journey, JOURNEY_NAMES[journey]);
    return () => uninstall();
    // Only install once per mount — journey changes don't re-install.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Keep URL in sync with in-memory journey state.
  useEffect(() => {
    if (!enabled) return;
    updateJourneyParam(journey);
  }, [enabled, journey]);

  // React to browser back/forward: re-read the URL and sync state.
  useEffect(() => {
    if (!enabled) return;
    const onPop = () => {
      const s = parseMoonshotParams(window.location.search, window.location.pathname);
      if (s.enabled) {
        setJourneyState(s.journey);
        setStepState(1);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [enabled]);

  const setJourney = useCallback((n: number) => {
    if (n < 1 || n > JOURNEY_COUNT) return;
    setJourneyState(n);
    setStepState(1);
  }, []);

  const nextJourney = useCallback(() => {
    setJourneyState((j) => {
      const next = j >= JOURNEY_COUNT ? 1 : j + 1;
      return next;
    });
    setStepState(1);
  }, []);

  const replayJourney = useCallback(() => {
    setStepState(1);
    setReplayNonce((n) => n + 1);
  }, []);

  const setStep = useCallback((n: number) => {
    if (n < 1) return;
    setStepState(n);
  }, []);

  // Keyboard shortcuts (J / K / 1..6). Ignored when focus is in an editable field.
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      if (tgt) {
        const tag = (tgt.tagName || '').toLowerCase();
        const editable = (tgt as HTMLElement).isContentEditable;
        if (tag === 'input' || tag === 'textarea' || editable) return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === 'j') {
        e.preventDefault();
        nextJourney();
      } else if (key === 'k') {
        e.preventDefault();
        replayJourney();
      } else if (/^[1-6]$/.test(key)) {
        e.preventDefault();
        setJourney(parseInt(key, 10));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, nextJourney, replayJourney, setJourney]);

  const fixture = journeyFixtures[journey] || journeyFixtures[1];
  const totalSteps = fixture.steps.length || 1;
  const journeyName = JOURNEY_NAMES[journey] || 'unknown';

  const value: DemoMockContextValue = useMemo(
    () => ({
      enabled,
      journey,
      journeyName,
      step: Math.min(step, totalSteps),
      totalSteps,
      hudOff,
      setJourney,
      nextJourney,
      replayJourney,
      setStep,
      mockFetch: rawMockFetch,
    }),
    // replayNonce is intentionally in the deps so children can key off of it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, journey, journeyName, step, totalSteps, hudOff, setJourney, nextJourney, replayJourney, setStep, replayNonce],
  );

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <DemoMockContext.Provider value={value}>
      {children}
      {!hudOff && <DebugHUD value={value} fixtureId={fixture.id} />}
    </DemoMockContext.Provider>
  );
};

export default DemoMockProvider;

/* ------------------------------------------------------------------------- */
/* Debug HUD                                                                  */
/* ------------------------------------------------------------------------- */

const hudStyle: React.CSSProperties = {
  position: 'fixed',
  top: 16,
  right: 16,
  width: 200,
  height: 60,
  padding: '8px 12px',
  background: 'rgba(0,0,0,0.5)',
  color: 'white',
  fontFamily: '"Space Grotesk", system-ui, sans-serif',
  fontSize: 12,
  lineHeight: 1.2,
  borderRadius: 8,
  zIndex: BOOT_OVERLAY_Z_INDEX, // Just under max so app modals can still go above in testing.
  pointerEvents: 'none',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  letterSpacing: 0.2,
};

const DebugHUD: React.FC<{ value: DemoMockContextValue; fixtureId: string }> = ({
  value,
  fixtureId,
}) => {
  return (
    <div style={hudStyle} data-moonshot-hud="1" aria-hidden>
      <div style={{ fontWeight: 600 }}>
        Journey {value.journey}/{JOURNEY_COUNT} · {value.journeyName}
      </div>
      <div style={{ opacity: 0.8 }}>
        step {value.step}/{value.totalSteps} · {fixtureId}
      </div>
    </div>
  );
};
